import { ChevronRight, CreditCard, Unplug } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { StripeAccount } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { CopyButton } from '@/shared/clipboard/copy-button';
import { SECTION_ANCHORS } from '@/shared/navigation/section-anchors';

import { useStripeAccount } from '../hooks/use-stripe-account';
import { useSetWebhookSecret, useStartStripeConnect } from '../hooks/use-stripe-mutations';
import { capabilityList, hasStripeAccount, isConnectUnavailable } from '../schemas/stripe-rules';

import { ApplePayDomains } from './apple-pay-domains';
import { DisconnectStripeDialog } from './disconnect-stripe-dialog';
import { StripeDirectKeysForm } from './stripe-direct-keys-form';
import { WebhookSecretDialog } from './webhook-secret-dialog';

const STATUS_STYLES: Record<NonNullable<StripeAccount['status']>, string> = {
  active: 'bg-success/15 text-success border-transparent dark:bg-success/20',
  pending: 'bg-warning/20 text-warning-foreground border-transparent dark:bg-warning/25',
  disabled: 'border-border text-muted-foreground',
};

interface ConnectedProps {
  storeId: string;
  account: StripeAccount;
  editable: boolean;
  primaryDomain: string | null;
}

function Connected({ storeId, account, editable, primaryDomain }: ConnectedProps) {
  const { t } = useTranslation('paymentMethods');
  const format = useFormat();
  const [disconnecting, setDisconnecting] = useState(false);
  const [replacingKeys, setReplacingKeys] = useState(false);
  const setSecret = useSetWebhookSecret(storeId);
  const capabilities = capabilityList(account);
  const endpoint = account.webhookUrl;
  const direct = account.mode === 'direct_keys';

  return (
    <div className="flex flex-col gap-4">
      {account.status === 'pending' ? (
        <Alert role="status">
          <AlertDescription>{t('stripe.pendingReview')}</AlertDescription>
        </Alert>
      ) : null}
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-[auto_1fr]">
        <dt className="text-muted-foreground">{t('stripe.fields.accountId')}</dt>
        <dd className="font-mono text-[13px] break-all">
          {account.accountId ?? <span className="text-muted-foreground">—</span>}
        </dd>
        <dt className="text-muted-foreground">{t('stripe.fields.mode')}</dt>
        <dd>{account.mode ? t(`stripe.mode.${account.mode}`) : '—'}</dd>
        <dt className="text-muted-foreground">{t('stripe.fields.publishableKey')}</dt>
        <dd className="font-mono text-[13px] break-all">
          {account.publishableKey ?? <span className="text-muted-foreground">—</span>}
        </dd>
        <dt className="text-muted-foreground">{t('stripe.fields.capabilities')}</dt>
        <dd>
          {capabilities.length === 0 ? (
            <span className="text-muted-foreground">{t('stripe.noCapabilities')}</span>
          ) : (
            <ul className="flex flex-wrap gap-1.5" aria-label={t('stripe.fields.capabilities')}>
              {capabilities.map((capability) => (
                <li key={capability.name}>
                  <Badge
                    variant="outline"
                    className={cn(
                      'font-mono text-[11px]',
                      capability.state === 'active' ? 'text-success' : 'text-muted-foreground',
                    )}
                  >
                    {capability.name}: {capability.state}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </dd>
        {direct ? (
          <>
            <dt className="text-muted-foreground">{t('stripe.fields.webhookUrl')}</dt>
            <dd className="flex flex-wrap items-center gap-2">
              <code className="font-mono text-[13px] break-all" data-testid="stripe-webhook-url">
                {endpoint}
              </code>
              <CopyButton value={endpoint} label={t('stripe.copyWebhookUrl')} size="icon-sm" />
              <span className="text-muted-foreground block basis-full text-xs">
                {t('stripe.webhookUrlHint')}
              </span>
            </dd>
            <dt id={SECTION_ANCHORS.stripeWebhook} className="text-muted-foreground">
              {t('stripe.fields.webhook')}
            </dt>
            <dd className="flex flex-col items-start gap-2">
              <span data-testid="stripe-webhook-secret">
                {account.webhookSecretSet
                  ? t('stripe.webhookConfigured')
                  : t('stripe.webhookMissing')}
              </span>
              {editable ? (
                <WebhookSecretDialog
                  webhookUrl={endpoint}
                  replacing={account.webhookSecretSet}
                  onSubmit={async (secret) => {
                    await setSecret.mutateAsync(secret);
                  }}
                />
              ) : null}
            </dd>
          </>
        ) : null}
        {account.updatedAt ? (
          <>
            <dt className="text-muted-foreground">{t('stripe.fields.updatedAt')}</dt>
            <dd title={format.utc(account.updatedAt)}>{format.relative(account.updatedAt)}</dd>
          </>
        ) : null}
      </dl>
      <ApplePayDomains storeId={storeId} editable={editable} suggestedDomain={primaryDomain} />
      {editable && direct ? (
        <div className="border-t pt-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="-ml-2"
            aria-expanded={replacingKeys}
            aria-controls="stripe-replace-keys"
            onClick={() => setReplacingKeys((open) => !open)}
          >
            <ChevronRight
              aria-hidden
              className={cn('transition-transform', replacingKeys && 'rotate-90')}
            />
            {t('stripe.keys.replaceToggle')}
          </Button>
          {replacingKeys ? (
            <div id="stripe-replace-keys" className="flex flex-col gap-3 pt-3">
              <p className="text-muted-foreground text-sm">{t('stripe.keys.replaceIntro')}</p>
              <StripeDirectKeysForm storeId={storeId} replacing />
            </div>
          ) : null}
        </div>
      ) : null}
      {editable ? (
        <>
          <div className="border-t pt-4">
            <Button variant="outline" onClick={() => setDisconnecting(true)}>
              <Unplug aria-hidden />
              {t('stripe.disconnect.action')}
            </Button>
          </div>
          <DisconnectStripeDialog
            storeId={storeId}
            open={disconnecting}
            onOpenChange={setDisconnecting}
          />
        </>
      ) : null}
    </div>
  );
}

/**
 * Without a platform Connect client id (`connectAvailable: false`) the API-key form leads. The
 * 409 from connect covers an account read before the platform setting changed.
 */
function NotConnected({
  storeId,
  connectAvailable,
  editable,
}: {
  storeId: string;
  connectAvailable: boolean;
  editable: boolean;
}) {
  const { t } = useTranslation(['paymentMethods', 'errors']);
  const message = useErrorMessage();
  const start = useStartStripeConnect(storeId);
  const [keysOpen, setKeysOpen] = useState(false);
  const unavailable = !connectAvailable || isConnectUnavailable(start.error);
  const requestId = isApiError(start.error) ? start.error.requestId : undefined;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted-foreground text-sm">{t('paymentMethods:stripe.notConnected')}</p>
      {!editable ? (
        <p className="text-muted-foreground text-sm">{t('paymentMethods:stripe.readOnly')}</p>
      ) : (
        <>
          {unavailable ? (
            <Alert role={connectAvailable ? 'alert' : 'status'}>
              <AlertTitle>{t('paymentMethods:stripe.unavailable.title')}</AlertTitle>
              <AlertDescription>
                {t('paymentMethods:stripe.unavailable.description')}
              </AlertDescription>
            </Alert>
          ) : start.isError ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>
                {message(start.error)}
                {requestId ? (
                  <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
                ) : null}
              </AlertDescription>
            </Alert>
          ) : null}
          {unavailable ? null : (
            <div>
              <Button
                onClick={() => start.mutate()}
                disabled={start.isPending || start.isSuccess}
                data-tour="settings.payment-methods.stripe-connect"
              >
                <CreditCard aria-hidden />
                {start.isPending || start.isSuccess
                  ? t('paymentMethods:stripe.connecting')
                  : t('paymentMethods:stripe.connect')}
              </Button>
            </div>
          )}
          <div className="border-t pt-3">
            {unavailable ? (
              <StripeDirectKeysForm storeId={storeId} />
            ) : (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="-ml-2"
                  aria-expanded={keysOpen}
                  aria-controls="stripe-direct-keys"
                  onClick={() => setKeysOpen((open) => !open)}
                >
                  <ChevronRight
                    aria-hidden
                    className={cn('transition-transform', keysOpen && 'rotate-90')}
                  />
                  {t('paymentMethods:stripe.keys.toggle')}
                </Button>
                {keysOpen ? (
                  <div id="stripe-direct-keys" className="pt-3">
                    <StripeDirectKeysForm storeId={storeId} />
                  </div>
                ) : null}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Card payments through Stripe Connect Standard: the owner connects their own Stripe account and
 * the storefront charges on it. Pasting API keys is the documented exception, folded away.
 */
export interface StripeCardProps {
  storeId: string;
  /** Connect, disconnect and Apple Pay changes need settings.write and an open store. */
  editable: boolean;
  /** Proposed as the first Apple Pay domain. */
  primaryDomain?: string | null;
}

export function StripeCard({ storeId, editable, primaryDomain = null }: StripeCardProps) {
  const { t } = useTranslation('paymentMethods');
  const query = useStripeAccount(storeId);
  const account = query.data;
  const linked = account ? hasStripeAccount(account) : false;

  return (
    <Card
      id={SECTION_ANCHORS.stripe}
      className="mb-6 gap-4"
      data-tour="settings.payment-methods.stripe"
      data-search-sections={`${SECTION_ANCHORS.stripeWebhook} ${SECTION_ANCHORS.applePay}`}
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="size-4" aria-hidden />
          {t('stripe.title')}
        </CardTitle>
        <CardDescription>{t('stripe.description')}</CardDescription>
        {account ? (
          <CardAction>
            {linked && account.status ? (
              <Badge variant="outline" className={cn(STATUS_STYLES[account.status])}>
                {t(`stripe.status.${account.status}`)}
              </Badge>
            ) : (
              <Badge variant="outline" className="text-muted-foreground">
                {t('stripe.status.notConnected')}
              </Badge>
            )}
          </CardAction>
        ) : null}
      </CardHeader>
      <CardContent>
        {query.isPending ? (
          <Skeleton className="h-24" />
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : account && linked ? (
          <Connected
            storeId={storeId}
            account={account}
            editable={editable}
            primaryDomain={primaryDomain}
          />
        ) : (
          <NotConnected
            storeId={storeId}
            connectAvailable={account?.connectAvailable ?? true}
            editable={editable}
          />
        )}
      </CardContent>
    </Card>
  );
}
